import {
  INGEST_STATUSES,
  type IngestStatus,
  isApiResource,
} from "@contract-manager/shared";
import { ApiError, type ErrorDetail } from "../api/client";
import type { MessageKey } from "../i18n/messages";
import type { MessageParams, Translate } from "../i18n/translate";
import { NotSignedInError } from "./cognitoTokenProvider";

/**
 * Whether this failure means "sign in again" rather than "something broke".
 *
 * Two shapes reach the UI: the provider refusing to produce a token, and the
 * API refusing the one it was given. They are the same thing to the person
 * looking at the screen, and neither is worth showing as an error message.
 */
export function isSignedOutError(error: unknown): boolean {
  return (
    error instanceof NotSignedInError ||
    (error instanceof ApiError && error.statusCode === 401)
  );
}

const BYTES_PER_MEGABYTE = 1024 * 1024;

/**
 * The message to show for anything that is not a sign-out.
 *
 * A coded failure is translated; anything else — a refusal this build has no
 * code for, a thrown `Error` — shows its own message, which is better than a
 * generic "something went wrong" that tells the reader nothing.
 */
export function describeError(
  error: unknown,
  t: Translate<MessageKey>,
): string {
  if (error instanceof ApiError && error.detail !== undefined) {
    return t(`error.${error.detail.code}`, paramsFor(error.detail, t));
  }
  return error instanceof Error ? error.message : String(error);
}

/** The params as the message wants them: nouns translated, sizes in MB. */
function paramsFor(
  detail: ErrorDetail,
  t: Translate<MessageKey>,
): MessageParams {
  const params = detail.params ?? {};
  const { resource, status, maxBytes } = params;
  return {
    ...params,
    ...(isApiResource(resource) ? { resource: t(`resource.${resource}`) } : {}),
    ...(isIngestStatus(status) ? { status: t(`ingest.${status}`) } : {}),
    ...(typeof maxBytes === "number"
      ? { maxMegabytes: Math.floor(maxBytes / BYTES_PER_MEGABYTE) }
      : {}),
  };
}

function isIngestStatus(value: unknown): value is IngestStatus {
  return (
    typeof value === "string" &&
    (INGEST_STATUSES as readonly string[]).includes(value)
  );
}
